import { gql } from '@apollo/client'

const address = gql`
  fragment Address on Address {
    id
    addressParts {
      street
      city
      state
      zipcode
    }
    geolocation {
      latitude
      longitude
      tzName
    }
  }
`

const botThread = gql`
  fragment BotThread on BotThread {
    sendTime
    showCountdown
    action {
      ...ActionToExec
    }
  }
`

const contact = gql`
  fragment Contact on Contact {
    id
    firstName
    lastName
    addressEmail
    addressPhone
    workHours
    language
  }
`

const firm = gql`
  fragment Firm on Firm {
    id
    firmName
    firmType
    pmTypes
    pmKeyJsons
    maxAcceptedPerShowing
    showingDurationMins
    acceptSection8
    tourTypes
    showingPolicy
    section8Text
    qualificationText
    petPolicyText
    moveInCostText
    discountsText
    applicationDocsText
    leaseTermsText
    cosignerPolicyText
    realtorText
    officePhone
    propertyListingsUrl
    englishConversationOnly
    headsUpText
  }
`

const exchangeCreds = gql`
  fragment ExchangeCreds on ExchangeCreds {
    email
    ewsUrl
    userName
    password
  }
`
const msGraphCreds = gql`
  fragment MsGraphCreds on MsGraphCreds {
    email
    clientId
    tokenCache
    homeAccountId
    clientSecret
    password
  }
`
const gSuiteCreds = gql`
  fragment GSuiteCreds on GSuiteCreds {
    email
    clientId
    refreshToken
    password
  }
`

const resmanInfo = gql`
  fragment BasicPmInfo on BasicPmInfo {
    pmType
    pmFirm
  }
`

const apmInfo = gql`
  fragment ApmInfo on ApmInfo {
    pmType
    pmFirm
    additionalVhosts
    showingsNotifyTenant
  }
`

const entrataInfo = gql`
  fragment EntrataInfo on EntrataInfo {
    pmType
    pmFirm
    username
    password
  }
`

const yardiInfo = gql`
  fragment YardiInfo on YardiInfo {
    pmType
    pmFirm
    serviceUrl
    serverName
    database
    databasePlatform
    apiEntity
    apiLicenseKey
    password
  }
`

const leasingTask = gql`
  fragment LeasingTask on LeasingTask {
    id
    displayName
    addressPhone
    addressEmail
    isArchived
    taskType
    isBotDisabled
    launchDate
    hasLaunched
    offboardDate
    hasOffboarded
    showingHours
    firmTaskId
    numUnits
    address {
      ...Address
    }
    streetViewAddress
    firm {
      ...Firm
    }
    taskAgents {
      id
      agent {
        ...Contact
      }
      precedence
      numTasks
      firmTaskThreadType
      workHours
    }
    primaryPmKeyJson
    leasingInfo {
      buildingName
      meetingDirections
      selfGuidedMeetingDirections
      areAgentsOnsite
      officePhone
      acceptSection8
      tourTypes
      showingPolicy
      section8Text
      applicationUrl
      propertyWebsite
      floorPlansUrl
      qualificationText
      petPolicyText
      moveInCostText
      discountsText
      applicationDocsText
      leaseTermsText
      cosignerPolicyText
      realtorText
      amenitiesText
      laundryText
      utilitiesText
      parkingText
      leasingSpecialsText
      leasingSpecials {
        text
        endDate
      }
      handicappedUnitsText
      yardiPropertyCode
      propertyType
      maxAcceptedPerShowing
      showingDurationMins
      isFurnished
      maxSimultaneousShowings
    }
    tourTypes
  }
`

const leasingTaskThreads = gql`
  fragment LeasingTaskThreads on LeasingTask {
    id
    isArchived
    displayName
    streetViewAddress
    threads {
      ...Thread
    }
  }
`

const message = gql`
  fragment Message on Message {
    id
    timeCreated
    addressFrom
    addressTo
    messageType
    messageStatus
    callStatus
    callStart
    callDuration
    isInbound
    censoredShortBody
    censoredTranslatedShortBody
    translatedEmailSubject
    emailSubject
    mediaUrl
    calEventId
    externalUrl
    author {
      id
      name
    }
    errorMessage
    actionId
    actionUrl
    classification
    tags
    outboundTags
    leasingAttributionSource
    messageLLMInfo
  }
`

const messageUserAnnotation = gql`
  fragment MessageUserAnnotation on MessageAnnotation {
    id
    timeCreated
    user {
      id
      userType
      name
    }
    classification
    tags
  }
`

const note = gql`
  fragment Note on Note {
    id
    timeCreated
    body
    author {
      id
      name
    }
    messageId
    threadId
    agentName
  }
`

const errorState = gql`
  fragment ErrorState on ErrorState {
    id
    timeCreated
    body
  }
`

const showing = gql`
  fragment Showing on Showing {
    id
    timeCreated
    timeStart
    leasingTaskId
    showingStatus
    calEventUrl
    showingContacts {
      calResponseStatus
      confirmationStatus
      isAgent
      timeUpdated
      contact {
        ...Contact
      }
    }
    unitType
    tourType
  }
`

const showingContactHistory = gql`
  fragment ShowingContactHistory on ShowingContactHistory {
    timeUpdated
    contactId
    confirmationStatus
    user {
      id
      name
    }
  }
`

const thread = gql`
  fragment Thread on Thread {
    id
    timeCreated
    messageType
    conversationType
    isArchived
    archiveUntil
    botStatus
    addressUs
    addressThem
    lastMessageId
    lastReadMessageId
    lastInboundMessageId
    lastMessageInbound
    lastMessageTime
    sortTime
    contactThem {
      ...Contact
    }
    botThread {
      showCountdown
      sendTime
    }
    preferredTourType
    recentActions(limit: 50) {
      id
      timeCreated
      actionName
      actionDisplayName
      user {
        id
        name
      }
      summary
    }
  }
`

const threadMessagesLimit = 50

const messagesWithLinks = gql`
  fragment MessagesWithLinks on MessagesWithLinks {
    messages {
      ...Message
    }
    messageLinks {
      phrase
      link {
        __typename
        ... on LeasingTaskLink {
          leasingTask {
            id
            leasingInfo {
              propertyType
            }
          }
        }
        ... on FloorPlanLink {
          leasingTask {
            id
            displayName
          }
        }
      }
    }
    waitingForShowing
  }
`

const threadMessages = gql`
  fragment ThreadMessages on Thread {
    id
    messageType
    conversationType
    addressUs
    addressThem
    isArchived
    archiveUntil
    botStatus
    lastMessageId
    lastReadMessageId
    lastMessageInbound
    messagesWithLinks(loadOptions: { limit: 50 }) {
      ...MessagesWithLinks
    }
    botThread {
      ...BotThread
    }
    contactThem {
      ...Contact
    }
    unitTypesInterested
    moveInDate
    additionalOccupants
    leasingAttributionSource
    calendar {
      id
      timeLastAccess
    }
  }
`

const user = gql`
  fragment User on User {
    id
    name
    userType
    permissions {
      canViewAdminPage
      canModifyLeasingSpecials
      canModifyNotes
      canViewSuperDashboard
      canViewSuperTaskDetails
      canViewCoreTaskInfo
      canViewCalEventUrls
      canCreateTask
      canViewBotAction
      canEditAgents
      canModifyOperatorShifts
      canModifySeniorOperatorShifts
      canRunBasicJobs
      canViewOccupancy
      canEditTaskDetails
      canViewUnitsAndMarketing
      canResendTcpaViolations
      canResendMassCancellationViolations
      canResendAllowedActionViolations
      canResendInvalidBodyViolations
    }
  }
`

const userAdminDetails = gql`
  fragment UserAdminDetails on User {
    id
    phone
    email
    name
    userType
    permissions {
      canViewAdminPage
      canModifyLeasingSpecials
      canModifyNotes
      canViewSuperDashboard
      canViewSuperTaskDetails
      canViewCoreTaskInfo
      canViewCalEventUrls
      canCreateTask
      canViewBotAction
      canEditAgents
      canRunBasicJobs
      canViewOccupancy
      canEditTaskDetails
      canViewUnitsAndMarketing
      canResendTcpaViolations
      canResendMassCancellationViolations
      canResendAllowedActionViolations
      canResendInvalidBodyViolations
      canModifyOperatorShifts
      canModifySeniorOperatorShifts
    }
    adpUserId
    adpFirstName
    adpLastName
    slackUserId
    githubUsername
    pagerDutyId
    teamName
  }
`

const actionToExec = gql`
  fragment ActionToExec on Action {
    actionName
    preview
    classification
    sendQueue
    commandJsons
    uuid
    logSummary
    hasArchiveCommand
    hasSnoozeCommand
    llmAction
    commandTypes
    commandPreviews
  }
`

const lookingAtFragment = gql`
  fragment LookingAtFragment on User {
    lookingAtUser {
      id
      name
    }
  }
`

const threadListItem = gql`
  fragment ThreadListItem on Thread {
    id
    conversationType
    isArchived
    botStatus
    lastReadMessageId
    lastInboundMessageId
    lastMessageInbound
    sortTime
    contactThem {
      id
      firstName
      lastName
      addressEmail
      addressPhone
    }
    botThread {
      showCountdown
      sendTime
    }
  }
`

const threadLeasingTask = gql`
  fragment ThreadLeasingTask on LeasingTask {
    id
    displayName
    isArchived
    taskType
    isBotDisabled
    launchDate
    hasLaunched
    offboardDate
    hasOffboarded
    showingHours
    firmTaskId
    address {
      id
      geolocation {
        tzName
      }
    }
    firm {
      id
      firmName
      pmTypes
      englishConversationOnly
      headsUpText
    }
    leasingInfo {
      areAgentsOnsite
      propertyWebsite
      leasingSpecials {
        text
        endDate
      }
      propertyType
    }
    tourTypes
    holidays
  }
`

const taskListItem = gql`
  fragment TaskListItem on LeasingTask {
    id
    isArchived
    displayName
    streetViewAddress
    leasingInfo {
      propertyType
    }
    threads {
      ...ThreadListItem
    }
  }
`

const taskDetail = gql`
  fragment TaskDetail on LeasingTask {
    addressPhone
    addressEmail
    taskType
    isBotDisabled
    launchDate
    hasLaunched
    offboardDate
    hasOffboarded
    showingHours
    firmTaskId
    streetViewAddress
    address {
      id
      addressParts {
        street
        city
        state
        zipcode
      }
      geolocation {
        tzName
      }
    }
    firm {
      id
      firmName
      pmTypes
      pmKeyJsons
      maxAcceptedPerShowing
      showingDurationMins
      acceptSection8
      tourTypes
      showingPolicy
      section8Text
      qualificationText
      petPolicyText
      moveInCostText
      discountsText
      applicationDocsText
      leaseTermsText
      cosignerPolicyText
      realtorText
      officePhone
      propertyListingsUrl
    }
    taskAgents {
      id
      agent {
        id
        firstName
        lastName
        addressEmail
        addressPhone
        workHours
      }
      precedence
      numTasks
      firmTaskThreadType
      isCurrentlyWorking
    }
    primaryPmKeyJson
    leasingInfo {
      buildingName
      meetingDirections
      selfGuidedMeetingDirections
      areAgentsOnsite
      officePhone
      acceptSection8
      tourTypes
      showingPolicy
      section8Text
      applicationUrl
      propertyWebsite
      floorPlansUrl
      qualificationText
      petPolicyText
      moveInCostText
      discountsText
      applicationDocsText
      leaseTermsText
      cosignerPolicyText
      realtorText
      amenitiesText
      laundryText
      utilitiesText
      parkingText
      leasingSpecialsText
      leasingSpecials {
        text
        endDate
      }
      handicappedUnitsText
      yardiPropertyCode
      propertyType
      maxAcceptedPerShowing
      showingDurationMins
      isFurnished
      maxSimultaneousShowings
    }
    tourTypes
  }
`

export const Fragments = {
  address,
  botThread,
  contact,
  errorState,
  firm,
  leasingTask,
  leasingTaskThreads,
  message,
  messageUserAnnotation,
  note,
  showing,
  showingContactHistory,
  thread,
  threadMessages,
  threadMessagesLimit,
  user,
  userAdminDetails,
  actionToExec,
  lookingAtFragment,
  threadListItem,
  threadLeasingTask,
  taskListItem,
  taskDetail,
  messagesWithLinks,
  exchangeCreds,
  msGraphCreds,
  gSuiteCreds,
  resmanInfo,
  apmInfo,
  entrataInfo,
  yardiInfo,
}
